import { AssessmentStatus } from "./api-data-types";

import { SERVER_API_PATH } from "api/api-constants";
import { serverApi } from "api/api-server-actions";
import {
  AllAssessmentTableRow,
  HomeAssessmentTableRow,
  OutcomeScoreTableData,
  PagePointTableData,
  SectionOutcomeIndexData,
  SectionScoreTableRow,
  StudentAssessmentScoreTableRow,
  StudentHomeAssessmentTableRow,
  StudentOutcomeScoreTableData,
  StudentSectionOutcomeTableData,
} from "api/api-types-assessment";
import { withApiErrorReporting } from "utils/api-errors";
import { CSVData } from "utils/file";
import { HomeRole } from "utils/urls";

/**
 * --------------------------------------------------------------------
 * Page Assessments
 * --------------------------------------------------------------------
 */

export async function serverGetAllAssessmentTable(
  sectionId: string,
): Promise<AllAssessmentTableRow[]> {
  return withApiErrorReporting("serverGetAllAssessmentTable", async () => {
    const response = await serverApi.get<AllAssessmentTableRow[]>(
      `${SERVER_API_PATH}/assessment/page_assessments/table/`,
      { params: { section_id: sectionId } },
    );
    return response.data;
  });
}

export async function serverGetAllAssessmentReport(params: {
  term_id?: string;
  course_id?: string;
  section_id?: string;
  report_filter?: AssessmentStatus;
}): Promise<CSVData> {
  return withApiErrorReporting("serverGetAllAssessmentReport", async () => {
    const response = await serverApi.get<CSVData>(
      `${SERVER_API_PATH}/assessment/page_assessments/report/`,
      { params },
    );
    return response.data;
  });
}

/**
 * --------------------------------------------------------------------
 * Outcome Scores
 * --------------------------------------------------------------------
 */

export async function serverGetOutcomeScoreTable(params: {
  section_id: string;
}): Promise<OutcomeScoreTableData> {
  return withApiErrorReporting("serverGetOutcomeScoreTable", async () => {
    const response = await serverApi.get<OutcomeScoreTableData>(
      `${SERVER_API_PATH}/assessment/outcome_scores/table/`,
      { params },
    );
    return response.data;
  });
}

export async function serverGetIndividualOutcomeScore(
  sectionId: string,
  sectionOutcomeId: string,
): Promise<number | null> {
  return withApiErrorReporting("serverGetIndividualOutcomeScore", async () => {
    const response = await serverApi.get<{ average_score: number | null }>(
      `${SERVER_API_PATH}/assessment/outcome_scores/individual/`,
      { params: { section_id: sectionId, section_outcome_id: sectionOutcomeId } },
    );
    return response.data.average_score;
  });
}

export async function serverGetOutcomeScoreReport(params: {
  section_id: string;
}): Promise<CSVData> {
  return withApiErrorReporting("serverGetOutcomeScoreReport", async () => {
    const response = await serverApi.get<CSVData>(
      `${SERVER_API_PATH}/assessment/outcome_scores/report/`,
      { params },
    );
    return response.data;
  });
}

/**
 * --------------------------------------------------------------------
 * Page Points
 * --------------------------------------------------------------------
 */

export async function serverGetPagePointTable(params: {
  section_id: string;
}): Promise<PagePointTableData> {
  return withApiErrorReporting("serverGetPagePointTable", async () => {
    const response = await serverApi.get<PagePointTableData>(
      `${SERVER_API_PATH}/assessment/page_points/table/`,
      { params },
    );
    return response.data;
  });
}

export async function serverGetPagePointReport(params: { section_id: string }): Promise<CSVData> {
  return withApiErrorReporting("serverGetPagePointReport", async () => {
    const response = await serverApi.get<CSVData>(
      `${SERVER_API_PATH}/assessment/page_points/report/`,
      { params },
    );
    return response.data;
  });
}

/**
 * --------------------------------------------------------------------
 * Section Scores
 * --------------------------------------------------------------------
 */

export async function serverGetSectionScoreTable(
  sectionId: string,
): Promise<SectionScoreTableRow[]> {
  return withApiErrorReporting("serverGetSectionScoreTable", async () => {
    const response = await serverApi.get<SectionScoreTableRow[]>(
      `${SERVER_API_PATH}/assessment/section_scores/table/`,
      { params: { section_id: sectionId } },
    );
    return response.data;
  });
}

export async function serverGetSectionScoreReport(params: {
  term_id?: string;
  course_id?: string;
  section_id?: string;
  report_type?: "admin" | "section";
}): Promise<CSVData> {
  return withApiErrorReporting("serverGetSectionScoreReport", async () => {
    const response = await serverApi.get<CSVData>(
      `${SERVER_API_PATH}/assessment/section_scores/report/`,
      { params },
    );
    return response.data;
  });
}

/**
 * --------------------------------------------------------------------
 * Student Assessment Scores
 * --------------------------------------------------------------------
 */

export async function serverGetHomeAssessmentTable(params: {
  term_id: string;
  /** Participant home persona; server rejects other values. */
  role: HomeRole;
}): Promise<HomeAssessmentTableRow[] | StudentHomeAssessmentTableRow[]> {
  return withApiErrorReporting("serverGetHomeAssessmentTable", async () => {
    const response = await serverApi.get<
      HomeAssessmentTableRow[] | StudentHomeAssessmentTableRow[]
    >(`${SERVER_API_PATH}/assessment/home_assessments/table/`, { params });
    return response.data;
  });
}

export async function serverGetStudentAssessmentScoreTable(
  sectionId: string,
  userId?: string | null,
): Promise<StudentAssessmentScoreTableRow[]> {
  return withApiErrorReporting("serverGetStudentAssessmentScoreTable", async () => {
    const params: { section_id: string; user_id?: string } = { section_id: sectionId };
    if (userId) {
      params.user_id = userId;
    }
    const response = await serverApi.get<StudentAssessmentScoreTableRow[]>(
      `${SERVER_API_PATH}/assessment/student_assessment_scores/table/`,
      { params },
    );
    return response.data;
  });
}

/**
 * --------------------------------------------------------------------
 * Student Outcome Scores
 * --------------------------------------------------------------------
 */

export async function serverGetStudentOutcomeScoreTable(
  sectionId: string,
  userId?: string | null,
): Promise<StudentOutcomeScoreTableData> {
  return withApiErrorReporting("serverGetStudentOutcomeScoreTable", async () => {
    const params: { section_id: string; user_id?: string } = { section_id: sectionId };
    if (userId) {
      params.user_id = userId;
    }
    const response = await serverApi.get<StudentOutcomeScoreTableData>(
      `${SERVER_API_PATH}/assessment/student_outcome_scores/table/`,
      { params },
    );
    return response.data;
  });
}

export async function serverGetStudentSectionOutcomeTable(
  sectionId: string,
  outcomeId: string,
): Promise<StudentSectionOutcomeTableData> {
  return withApiErrorReporting("serverGetStudentSectionOutcomeTable", async () => {
    const params = { section_id: sectionId, outcome_id: outcomeId };

    const response = await serverApi.get<StudentSectionOutcomeTableData>(
      `${SERVER_API_PATH}/assessment/student_section_outcomes/table/`,
      { params },
    );

    return response.data;
  });
}

export async function serverGetSectionOutcomeIndex(
  sectionId: string,
  sectionOutcomeId: string,
): Promise<SectionOutcomeIndexData> {
  return withApiErrorReporting("serverGetSectionOutcomeIndex", async () => {
    const params = { section_id: sectionId, section_outcome_id: sectionOutcomeId };

    const response = await serverApi.get<SectionOutcomeIndexData>(
      `${SERVER_API_PATH}/assessment/section_outcomes/index/`,
      { params },
    );

    return response.data;
  });
}
